import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { PhoneNumberInput } from '@change/ds-forms';
import { OpenInNewIcon } from '@change/ds-icons';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { createAnchorToLinkReplacer } from '#src/shared/ds';

import type { UseSmsOptInFormReturn } from '#src/app/shared/hooks/phoneOptIn';
import { isPhoneNumberCountry } from '#src/app/shared/utils/phoneCollection';

/** The SMS opt-in ask. Drive it with `useSmsOptInForm`. */
export const SmsOptInScreen: React.FC<SmsOptInScreenProps> = ({
	data,
	onChange,
	inputRef,
	showHeading = true,
	placeholder,
	children,
}) => {
	const { translate } = useI18n();

	const defaultCountry = isPhoneNumberCountry(data.countryCode) ? data.countryCode : undefined;
	const label = translate('corgi.petition.starter_onboarding.phone_collection_step_sms.input_label');

	return (
		<div className="flex flex-col gap-8">
			{showHeading && (
				<div className="flex flex-col gap-4">
					<Text variant="heading" size="large" as="h1">
						<Translate value="corgi.petition.starter_onboarding.phone_collection_step_sms.title" />
					</Text>
					<Text size="large" className="text-typography-weak">
						<Translate value="corgi.petition.starter_onboarding.phone_collection_step_sms.description" />
					</Text>
				</div>
			)}
			<div className="flex flex-col gap-4">
				<div>
					<div className="mb-1 flex items-center gap-1">
						<Text>
							<Translate value="corgi.petition.starter_onboarding.phone_collection_step_sms.input_label" />
						</Text>
						{!data.hasPhone && (
							<Text size="small" className="text-typography-weak">
								<Translate value="corgi.petition.starter_onboarding.phone_collection_step_sms.optional_label" />
							</Text>
						)}
					</div>
					<PhoneNumberInput
						ref={inputRef}
						name="starter-onboarding-phone-collection-sms-input"
						label={label}
						labelIsVisible={false}
						data-testid="starter-onboarding-phone-collection-sms-input"
						data-qa="starter-onboarding-phone-collection-sms-input"
						defaultCountry={defaultCountry}
						placeholder={placeholder}
						value={data.inputValue || undefined}
						onChange={onChange}
						errorMessage={data.inputError}
						isInvalid={Boolean(data.inputError)}
						size="large"
					/>
				</div>

				<Text as="div" size="x-small" className="text-typography-weak">
					<Translate value="corgi.petition.starter_onboarding.phone_collection_step_sms.sms_disclosure" />{' '}
					<TranslateHtml
						value="corgi.petition.starter_onboarding.phone_collection_step_sms.stop_help"
						replacements={{ tosUrl: '/policies/terms-of-service#10', privacyUrl: '/policies/privacy' }}
						replace={createAnchorToLinkReplacer({
							size: 'x-small',
							className: 'text-typography-weak',
							trailingIcon: <OpenInNewIcon />,
						})}
						allowLinkTargetAttr
					/>
				</Text>

				{children}
			</div>
		</div>
	);
};

type SmsOptInScreenProps = Readonly<{
	data: UseSmsOptInFormReturn['data'];
	onChange: (value: string | undefined) => void;
	inputRef: React.Ref<HTMLInputElement>;
	/**
	 * Whether the screen renders its own headline and subhead. Set false when the host already shows
	 * them, so they are not rendered twice.
	 */
	showHeading?: boolean;
	placeholder?: string;
	/** The actions row, rendered below the disclosure. Omit it to render actions outside the screen. */
	children?: React.ReactNode;
}>;
